import React, { useState, useEffect } from 'react';
import { Accordion, AccordionSummary, AccordionDetails, FormControlLabel, CircularProgress } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axios from 'axios';
import './BusesCheckBoxesModified.css'; // Archivo CSS renombrado

const BusesCheckBoxesModified = ({ selectedElements, handleSelectElement, handleRemoveElement }) => {
  const [modifiedBusLines, setModifiedBusLines] = useState([]);
  const [modifiedSelectedLines, setModifiedSelectedLines] = useState([]);
  const [modifiedLoadingLines, setModifiedLoadingLines] = useState({});
  const [modifiedIsInitialLoading, setModifiedIsInitialLoading] = useState(true);
  const [modifiedActiveZones, setModifiedActiveZones] = useState([]);

  // Definición de las zonas y sus líneas
  const modifiedBusZones = {
    'Norte': ['21', '29', '44', '60', '76', '78', '87', '93', '108', '117', '118', '130', '204', '228', '341', '391', '720', '722', '723'],
    'Sur': ['26','31', '33', '45', '46', '70', '85','98', '100', '115', '119', '150', '164', '179', '188', '271', '295', '299', '324', '384', '421', '501', '524', '527'],
    'Oeste': ['1', '8', '23', '25', '46', '91', '99', '115','132', '135', '150', '244', '302', '303', '390', '461', '462', '463', '464'],
    'Todos': []
  };

  // Cargar todas las líneas base disponibles
  useEffect(() => {
    const fetchModifiedBaseLines = async () => {
      try {
        setModifiedIsInitialLoading(true);
        
        const response = await axios.get('https://mapa.ne3publicidad.com.ar/api/api/getLinea/.*');
        
        const baseLinesMap = new Map();
        
        response.data.forEach(item => {
          const baseLine = item.linea.replace(/[^0-9]/g, '');
          if (baseLine) {
            if (!baseLinesMap.has(baseLine)) {
              baseLinesMap.set(baseLine, {
                base: baseLine,
                color: item.color || '#FF0000'
              });
            }
          }
        });
  
        // Convertir a array y ordenar numéricamente
        const uniqueBaseLines = Array.from(baseLinesMap.values())
          .sort((a, b) => {
            const numA = parseInt(a.base, 10);
            const numB = parseInt(b.base, 10);
            return numA - numB;
          });
  
        setModifiedBusLines(uniqueBaseLines);
        
      } catch (error) {
        console.error("Error al obtener líneas base:", error);
        
        try {
          const alternativeResponse = await axios.get('https://mapa.ne3publicidad.com.ar/api/api/getLineasDistinct');
          const lines = alternativeResponse.data.map(item => ({
            base: item.linea.replace(/[^0-9]/g, ''),
            color: item.color || '#FF0000'
          })).filter(line => line.base);
          
          // Eliminar duplicados y ordenar
          const uniqueLines = lines
            .filter((line, index, self) => 
              index === self.findIndex(l => l.base === line.base)
            )
            .sort((a, b) => {
              const numA = parseInt(a.base, 10);
              const numB = parseInt(b.base, 10);
              return numA - numB;
            });
          
          setModifiedBusLines(uniqueLines);
        } catch (altError) {
          console.error("Método alternativo falló:", altError);
        }
      } finally {
        setModifiedIsInitialLoading(false);
      }
    };
  
    fetchModifiedBaseLines();
  }, []);

  // Efecto para manejar la selección de zonas
  useEffect(() => {
  if (modifiedIsInitialLoading) return;

  const handleModifiedZoneSelection = async () => {
    // Si se seleccionó "Todos", manejar de manera especial
    if (modifiedActiveZones.includes('Todos')) {
      // Seleccionar todas las líneas
      const allLines = modifiedBusLines.map(line => line.base);
      const linesToAdd = allLines.filter(line => !modifiedSelectedLines.includes(line));
      
      for (const line of linesToAdd) {
        await fetchModifiedLineData(line);
      }
      
      return;
    }

    // Si no hay zonas seleccionadas, no hacer nada
    if (modifiedActiveZones.length === 0) return;

    // Obtener todas las líneas exactas de las zonas seleccionadas
    const zoneLines = modifiedActiveZones.flatMap(zone => modifiedBusZones[zone]);
    
    // Filtrar líneas que ya están seleccionadas
    const linesToAdd = zoneLines.filter(line => !modifiedSelectedLines.includes(line));
    
    // Filtrar líneas que están seleccionadas pero no están en las zonas seleccionadas
    const linesToRemove = modifiedSelectedLines.filter(line => 
      !zoneLines.includes(line)
    );
    
    // Eliminar líneas que no pertenecen a las zonas seleccionadas
    linesToRemove.forEach(line => {
      handleRemoveElement(line, 'bus');
      setModifiedSelectedLines(prev => prev.filter(l => l !== line));
    });
    
    // Agregar nuevas líneas de las zonas seleccionadas
    for (const line of linesToAdd) {
      await fetchModifiedLineData(line);
    }
  };

  handleModifiedZoneSelection();
}, [modifiedActiveZones, modifiedBusLines]);

  const fetchModifiedLineData = async (baseLine) => {
    try {
      setModifiedLoadingLines(prev => ({ ...prev, [baseLine]: true }));
      
      const response = await axios.get(`https://mapa.ne3publicidad.com.ar/api/api/getLinea/${baseLine}`);
      
      const variantsMap = new Map();
      
      response.data.forEach(point => {
        const variant = point.linea.replace(baseLine, '') || 'A';
        if (!variantsMap.has(variant)) {
          variantsMap.set(variant, {
            points: [],
            color: point.color || '#FF0000'
          });
        }
        variantsMap.get(variant).points.push({
          lat: parseFloat(point.lat),
          lng: parseFloat(point.lng),
          sequence: point.sequence_order || 0
        });
      });
  
      variantsMap.forEach((variantData, variant) => {
        const fullLine = `${baseLine}${variant === 'A' ? '' : variant}`;
        const points = variantData.points.sort((a, b) => a.sequence - b.sequence);
        
        const cleanedPath = [];
        const pointMap = new Map();
  
        points.forEach(point => {
          const key = `${point.lat},${point.lng}`;
          if (!pointMap.has(key)) {
            pointMap.set(key, true);
            cleanedPath.push(point);
          }
        });
  
        if (cleanedPath.length > 2) {
          const first = cleanedPath[0];
          const last = cleanedPath[cleanedPath.length - 1];
          const distance = Math.sqrt(
            Math.pow(last.lat - first.lat, 2) + 
            Math.pow(last.lng - first.lng, 2)
          );
          
          if (distance < 0.0001) {
            cleanedPath.pop();
          }
        }
  
        if (cleanedPath.length > 0) {
          handleSelectElement(fullLine, cleanedPath, 'bus', variantData.color);
        }
      });
      
      // Ordenar las líneas seleccionadas al añadir una nueva
      setModifiedSelectedLines(prev => [...prev, baseLine]
        .sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
      );
    } catch (error) {
      console.error(`Error al cargar línea ${baseLine}:`, error);
    } finally {
      setModifiedLoadingLines(prev => ({ ...prev, [baseLine]: false }));
    }
  };

  const handleModifiedLineToggle = (baseLine) => {
  // Si "Todos" está marcado, desmarcarlo primero
  if (modifiedActiveZones.includes('Todos')) {
    setModifiedActiveZones(prev => prev.filter(z => z !== 'Todos'));
  }

  if (modifiedSelectedLines.includes(baseLine)) {
    // Eliminar solo esta línea base exacta y sus variantes
    handleRemoveElement(baseLine, 'bus');
    setModifiedSelectedLines(prev => prev.filter(l => l !== baseLine));
  } else {
    fetchModifiedLineData(baseLine);
  }
 };

  const handleModifiedZoneToggle = (zone) => {
    setModifiedActiveZones(prev => {
      if (zone === 'Todos') {
        // Si se selecciona "Todos", deseleccionar otras zonas
        return prev.includes('Todos') ? [] : ['Todos'];
      } else {
        // Si se selecciona otra zona, deseleccionar "Todos" si está seleccionado
        let newZones = prev.includes('Todos') ? [] : [...prev];
        
        if (newZones.includes(zone)) {
          return newZones.filter(z => z !== zone);
        } else {
          return [...newZones, zone];
        }
      }
    });
  };

  if (modifiedIsInitialLoading) {
    return (
      <div className="modified-accordion-container">
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} className="modified-accordion-button">
            <h3>Líneas de Colectivos </h3>
          </AccordionSummary>
          <AccordionDetails className="modified-accordion-details">
            <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
              <CircularProgress />
            </div>
          </AccordionDetails>
        </Accordion>
      </div>
    );
  }

  return (
    <div className="modified-accordion-container">
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} className="modified-accordion-button">
          <h3>Líneas de Colectivos</h3>
        </AccordionSummary>
        <AccordionDetails className="modified-accordion-details">
          <div className="modified-checkbox-container">
            {modifiedBusLines.map(line => (
              <div key={line.base} className="modified-route-group">
                <div
                  className={`modified-custom-checkbox ${modifiedSelectedLines.includes(line.base) ? 'modified-selected' : ''}`}
                  onClick={() => {
                    if (!modifiedLoadingLines[line.base]) {
                      handleModifiedLineToggle(line.base);
                    }
                  }}
                  style={{
                    cursor: modifiedLoadingLines[line.base] ? 'wait' : 'pointer',
                    opacity: modifiedLoadingLines[line.base] ? 0.7 : 1
                  }}
                >
                  {line.base}
                  {modifiedLoadingLines[line.base] && (
                    <CircularProgress size={16} style={{ marginLeft: 8, color: line.color }} />
                  )}
                </div>
              </div>
            ))}
          </div>
          
          {/* Checkboxes de zonas */}
          <div className="modified-zones-container">
            {Object.keys(modifiedBusZones).map(zone => (
              <div key={zone} className="modified-zone-group">
                <div
                  className={`modified-custom-checkbox modified-zone-checkbox ${modifiedActiveZones.includes(zone) ? 'modified-selected' : ''}`}
                  onClick={() => handleModifiedZoneToggle(zone)}
                >
                  {zone}
                </div>
              </div>
            ))}
          </div>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

export default BusesCheckBoxesModified;